/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useMemo, useCallback, useState, useEffect } from 'react'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import {
  Team,
  useSearchAdminTeams,
} from '@sportninja/common/hooks/useSearchAdminTeams'
import { AutoComplete, Spin, Tooltip, Alert } from 'antd'
import debounce from 'lodash/debounce'
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import Helmet from 'src/components/Helmet'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { MergeTeamSelector } from './components/MergeTeamSelector'
import Button from 'src/components/ts-components/Button'
import ReviewMergeModal from './components/ReviewMergeModal'
import { getAllGenericSportProperties } from '@sportninja/common/selectors/types'
import { getSports } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import { useSports } from '@sportninja/common/hooks/useNewSports'
import { Link, useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'

interface SelectedProperties {
  [key: string]: {
    teamId: string
    value: string
  }
}

interface TeamOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

const MergeTeams = ({ location, genericSports, sports }) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [selectedTeams, setSelectedTeams] = useState<any[]>([])
  const [selectedTeamToKeep, setSelectedTeamToKeep] = useState<string>()
  const [searchValue, setSearchValue] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [selectedProperties, setSelectedProperties] =
    useState<SelectedProperties>({})
  const { results, loading, search, clearResults } = useSearchAdminTeams()
  const organizationId = location?.state?.organizationId
  const organizationName = location?.state?.organizationName

  const { findSport } = useSports(sports, genericSports)
  const history = useHistory()

  // Reset selected properties when teams change
  useEffect(() => {
    setSelectedProperties({})
  }, [selectedTeams])

  // Handle Escape key press to go back
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        history.push(`${ROUTES.ORG_ROOT}/${organizationId}`)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [history, organizationId])

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length >= 2) {
          setHasSearched(true)
          search({
            query: value,
            organization_id: organizationId,
            page: 1,
          })
        }
      }, 500),
    [organizationId, search]
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      if (value.length < 3) {
        setHasSearched(false)
      }
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  // Helper function to check if a team has a different sport than the first selected team
  const isTeamFromDifferentSport = useCallback(
    (team: Team) => {
      if (selectedTeams.length === 0) {
        return false
      }

      const firstTeam = selectedTeams[0]
      const firstTeamSport = findSport(
        firstTeam.sport_id || '',
        firstTeam.generic_sport_id || ''
      )
      const teamSport = findSport(
        team.sport_id || '',
        team.generic_sport_id || ''
      )

      // If either sport is generic, compare generic_sport_ids
      if (firstTeamSport?.is_generic || teamSport?.is_generic) {
        return firstTeamSport?.generic_sport_id !== teamSport?.generic_sport_id
      }

      // Otherwise compare sport_ids directly
      return (team.sport_id || '') !== (firstTeam.sport_id || '')
    },
    [selectedTeams, findSport]
  )

  const handleSelect = useCallback(
    (value: string) => {
      const team = results?.find((item) => item.id === value)
      if (
        team &&
        !selectedTeams.find((t) => t.id === team.id) &&
        selectedTeams.length < 5 &&
        !isTeamFromDifferentSport(team)
      ) {
        setSelectedTeams((prev) => [...prev, team])
        setSearchValue('')
      }
    },
    [results, selectedTeams, isTeamFromDifferentSport]
  )

  const handleTeamSelect = useCallback((teamId: string) => {
    setSelectedTeamToKeep(teamId)
  }, [])

  const handleRemoveTeam = useCallback(
    (teamId: string) => {
      setSelectedTeams((prev) => prev.filter((team) => team.id !== teamId))
      if (selectedTeamToKeep === teamId) {
        setSelectedTeamToKeep(undefined)
      }
    },
    [selectedTeamToKeep]
  )

  const handlePropertySelect = useCallback(
    (property: string, teamId: string, value: string) => {
      setSelectedProperties((prev) => {
        // If this property is already selected for this team, unselect it
        if (prev[property]?.teamId === teamId) {
          const { [property]: _, ...rest } = prev
          return rest
        }

        return {
          ...prev,
          [property]: { teamId, value },
        }
      })
    },
    []
  )

  // Helper function to get all unique values for a property across teams
  const getPropertyValues = useCallback(
    (property: string) => {
      const values = new Map<string, string>() // value -> teamId

      selectedTeams.forEach((team) => {
        let value: string | undefined

        switch (property) {
          case 'daysmart_id':
            value = team.daysmart_id
            break
          case 'salesforce_id':
            value = team.salesforce_id
            break
          case 'email':
            value = team.email
            break
          case 'phone':
            value = team.phone
            break
          case 'website':
            value = team.website
            break
          default:
            return
        }

        if (value && value.trim()) {
          values.set(value, team.id)
        }
      })

      return values
    },
    [selectedTeams]
  )

  // Helper function to get properties that require user selection (multiple values)
  // When multiple teams have different values for the same property, user must choose which one to keep
  const getPropertiesRequiringSelection = useCallback(() => {
    const properties = [
      'daysmart_id',
      'salesforce_id',
      'email',
      'phone',
      'website',
    ]
    return properties.filter((property) => {
      const values = getPropertyValues(property)
      return values.size > 1 // More than one unique value
    })
  }, [getPropertyValues])

  // Helper function to get properties that should be auto-selected (single value)
  // When only one team has a value for a property, it should be automatically selected
  const getAutoSelectableProperties = useCallback(() => {
    const properties = [
      'daysmart_id',
      'salesforce_id',
      'email',
      'phone',
      'website',
    ]
    const autoSelectable: { [key: string]: { teamId: string; value: string } } =
      {}

    properties.forEach((property) => {
      const values = getPropertyValues(property)
      if (values.size === 1) {
        const [value, teamId] = Array.from(values.entries())[0]
        autoSelectable[property] = { teamId, value }
      }
    })

    return autoSelectable
  }, [getPropertyValues])

  const hasDifferentSports = useMemo(() => {
    if (selectedTeams.length < 2) {
      return false
    }

    return selectedTeams.some((team) => isTeamFromDifferentSport(team))
  }, [selectedTeams, isTeamFromDifferentSport])

  // Check if all required properties have been selected
  const areRequiredPropertiesSelected = useMemo(() => {
    const requiredProperties = getPropertiesRequiringSelection()
    return requiredProperties.every((property) => selectedProperties[property])
  }, [getPropertiesRequiringSelection, selectedProperties])

  const canMerge = useMemo(() => {
    return (
      selectedTeams.length >= 2 &&
      selectedTeamToKeep &&
      !hasDifferentSports &&
      areRequiredPropertiesSelected
    )
  }, [
    selectedTeams.length,
    selectedTeamToKeep,
    hasDifferentSports,
    areRequiredPropertiesSelected,
  ])

  const handleMerge = useCallback(() => {
    if (!canMerge) {
      return
    }

    // Auto-select properties with single values before opening the modal
    const autoSelectable = getAutoSelectableProperties()
    setSelectedProperties((prev) => ({
      ...prev,
      ...autoSelectable,
    }))

    setIsReviewModalOpen(true)
  }, [canMerge, getAutoSelectableProperties])

  const handleMergeComplete = useCallback(() => {
    setSelectedTeams([])
    setSelectedTeamToKeep(undefined)
    setIsReviewModalOpen(false)
    clearResults()
    setSearchValue('')
    setHasSearched(false)
  }, [clearResults])

  // Add this constant for the tooltip message
  const MAX_TEAMS_MESSAGE =
    'Maximum Of 5 Teams Allowed For Merging. Remove A Team To Add Another.'

  const formatTeamOption = useCallback(
    (team: Team, disabled = false): TeamOption => {
      return {
        value: team.id,
        disabled,
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
              width: 100%;
              padding: 4px 0;
              opacity: ${disabled ? 0.5 : 1};
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
                margin-bottom: 8px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <img
                  src={
                    getImageThumbnailId(team) ||
                    '/images/generic_placeholders/placeholder_team.png'
                  }
                  alt='Team Logo'
                  css={css`
                    width: 24px;
                    height: 24px;
                    border-radius: 8px;
                    border: 2px solid ${colors.WHITE};
                  `}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
                <div
                  css={css`
                    word-break: break-word;
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={disabled ? colors.NEUTRAL_100 : colors.WHITE}
                  >
                    {team.name_full || team.name}
                  </Text>
                </div>
              </div>
            </div>
            {team.id && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                ID: {team.id}
              </Text>
            )}
            {team?.hierarchy?.length > 0 && (
              <div
                css={css`
                  word-break: break-word;
                  white-space: normal;
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_100}
                  style={'text-transform: capitalize;'}
                >
                  {team?.hierarchy?.map((h) => h.name).join(' | ')}
                </Text>
              </div>
            )}
            {team.rosters && team.rosters.length > 0 && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                `}
              >
                {team.rosters.map((roster) => (
                  <div
                    key={roster.id}
                    css={css`
                      display: flex;
                      flex-direction: row;
                      align-items: flex-start;
                      gap: 4px;
                      flex-wrap: wrap;
                    `}
                  >
                    <div
                      css={css`
                        word-break: break-word;
                      `}
                    >
                      <Text
                        variant='paragraphXSmall'
                        weight='regular'
                        color={colors.NEUTRAL_100}
                      >
                        {roster.schedule}
                      </Text>
                    </div>
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      ({roster.players_count} Players |{' '}
                      {roster.team_officials_count} Officials)
                    </Text>
                  </div>
                ))}
              </div>
            )}
            {(!team.rosters || team.rosters.length === 0) && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                No Active Rosters
              </Text>
            )}
            {disabled && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.WARNING_300}
              >
                Different Sport - Cannot Merge
              </Text>
            )}
          </div>
        ),
      }
    },
    []
  )

  const teamOptions = useMemo(() => {
    // Filter out already selected teams from the results
    const availableTeams = results?.filter(
      (team) =>
        !selectedTeams.some((selectedTeam) => selectedTeam.id === team.id)
    )
    return (
      availableTeams?.map((team) => {
        const isDisabled = isTeamFromDifferentSport(team)
        return formatTeamOption(team, isDisabled)
      }) || []
    )
  }, [results, formatTeamOption, selectedTeams, isTeamFromDifferentSport])

  return (
    <div
      className='merge-teams'
      css={css`
        border-radius: 8px;
        width: 100%;
        height: 100%;
        padding: 64px 80px;
        ${isMobile &&
        css`
          padding: 16px 8px;
        `}
      `}
    >
      <Helmet title='Merge Teams' />
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          padding-bottom: 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_200};

          ${isMobile &&
          css`
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          `}
        >
          <Link
            to={`${ROUTES.ORG_ROOT}/${organizationId}`}
            aria-label='Go back to organization'
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 4px;
            `}
          >
            <ArrowLeftOutlined
              css={css`
                font-size: 16px;
                color: ${colors.PRIMARY};
              `}
            />
            <Text
              variant='captionSmall'
              weight='semiBold'
              color={colors.PRIMARY}
              style={'text-transform: uppercase;'}
            >
              Go Back TO {organizationName}
            </Text>
          </Link>
          <Text
            variant='displaySmall'
            weight='semiBold'
            color={colors.NEUTRAL_0}
          >
            Merge Teams
          </Text>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Search And Select Teams To Merge
          </Text>
        </div>
        <div
          css={css`
            min-width: 200px;
            ${isMobile &&
            css`
              width: 100%;
            `}
          `}
        >
          <Tooltip
            title={selectedTeams.length >= 5 ? MAX_TEAMS_MESSAGE : ''}
            placement='bottom'
          >
            <div>
              <AutoComplete
                value={searchValue}
                disabled={selectedTeams.length >= 5}
                suffixIcon={<SearchOutlined />}
                listHeight={800}
                css={css`
                  width: 100%;
                  min-width: 400px;
                  .ant-select-selector {
                    background: ${colors.SECONDARY_200} !important;
                    border-radius: 8px !important;
                    height: 40px !important;
                    cursor: ${selectedTeams.length >= 5
                      ? 'not-allowed'
                      : 'pointer'} !important;
                  }
                  .ant-select-selection-search {
                    display: flex;
                    align-items: center;
                  }
                  .ant-select-selection-search-input {
                    height: 48px !important;
                    font-size: 14px !important;
                    color: ${colors.WHITE} !important;
                  }
                  .ant-select-selection-placeholder {
                    line-height: 48px !important;
                    color: ${colors.NEUTRAL_50} !important;
                  }
                  .ant-select-item-option-content {
                    font-size: 14px !important;
                    font-weight: 400 !important;
                    color: ${colors.WHITE} !important;
                  }
                  &.ant-select-disabled .ant-select-selector {
                    opacity: 0.5;
                  }
                `}
                placeholder='Search Team'
                onSearch={handleSearch}
                onSelect={handleSelect}
                options={teamOptions}
                notFoundContent={
                  loading ? (
                    <Spin size='small' />
                  ) : hasSearched && searchValue.length >= 2 ? (
                    'No Teams Found'
                  ) : null
                }
                allowClear
                onClear={() => {
                  setSearchValue('')
                  setHasSearched(false)
                }}
              />
            </div>
          </Tooltip>
        </div>
      </div>

      {selectedTeams.length === 0 ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 48px;
            text-align: center;
            margin-top: 32px;
          `}
        >
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Use The Search Box Above To Select 2 To 5 Teams To Merge Together.
          </Text>
        </div>
      ) : (
        <div
          css={css`
            margin-top: 32px;
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <MergeTeamSelector
            teams={selectedTeams}
            onTeamSelect={handleTeamSelect}
            selectedTeamId={selectedTeamToKeep}
            onRemoveTeam={handleRemoveTeam}
            findSport={findSport}
            selectedProperties={selectedProperties}
            onPropertySelect={handlePropertySelect}
            requiredProperties={getPropertiesRequiringSelection()}
          />

          {hasDifferentSports && (
            <Alert
              description='Please Select Teams From The Same Sport To Merge Them Together.'
              type='warning'
              showIcon
              css={css`
                margin-bottom: 16px;
                max-width: 50%;
                align-self: center;
                ${isMobile &&
                css`
                  max-width: 100%;
                `}
              `}
            />
          )}

          {!areRequiredPropertiesSelected &&
            selectedTeams.length >= 2 &&
            selectedTeamToKeep &&
            !hasDifferentSports && (
              <Alert
                description={`Please Select Values For Properties That Have Multiple Options: ${getPropertiesRequiringSelection()
                  .map((prop) =>
                    prop
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  )
                  .join(', ')}.`}
                type='info'
                showIcon
                css={css`
                  margin-bottom: 16px;
                  max-width: 70%;
                  align-self: center;
                  ${isMobile &&
                  css`
                    max-width: 100%;
                  `}
                `}
              />
            )}

          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              padding-top: 16px;
              width: 250px;
              align-self: center;
            `}
          >
            <Button
              variant='primary'
              disabled={!canMerge}
              onClick={handleMerge}
              text='Review & Merge'
              isLoading={loading}
            />
          </div>
        </div>
      )}

      <ReviewMergeModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        teams={selectedTeams}
        selectedTeamId={selectedTeamToKeep}
        onMergeComplete={handleMergeComplete}
        selectedProperties={selectedProperties}
        findSport={findSport}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    genericSports: getAllGenericSportProperties(state),
    sports: Object.values(getSports(state)),
  }
}

export default connect(mapStateToProps, null)(MergeTeams)
